// Devices page (UX по образцу старого Sputnik-кабинета продаж). По умолчанию
// — устройства всех кабинетов с поиском по ним (устройство ищут, не зная,
// чьё оно); селектор сверху сужает список до одного кабинета. Чекбоксами
// выбираем N штук, в том числе из разных кабинетов → sticky-bar внизу
// «Перенести в другой кабинет» → модалка пакетного переноса (циклом по
// выбранным).
//
// Транспорт переноса не пакетирован на бэке намеренно: каждая панель идёт
// отдельным adminTransferDevice (Shanyrak transfer → wipe keys/codes/faces
// → GEO ownership flip → audit). Параллельно через Promise.allSettled —
// частичный успех видим и одна сбойная панель не валит остальные.

import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { gql } from "../lib/gql";
import {
  PANEL_FIRMWARE_QUERY,
  PHASE_LABEL,
  firmwarePanel,
  loadFirmwareAccess,
  phaseActive,
  type PanelFirmware,
} from "../lib/firmware";
import { FirmwareModal } from "./FirmwareModal";
import { BulkFirmwareModal } from "./BulkFirmwareModal";

type AdminDevice = {
  id: string;
  name: string;
  model: string;
  protocol: string;
  deviceType: string;
  motherboardId: string; // RTS-панель: 10-знач. номер на корпусе.
  agentSerial: string;   // RTS-камера: серийник.
  pointId: string | null;
  pointName: string;
  // Группы, на которых устройство висит калиткой; null — не калитка.
  gateGroupName: string | null;
  ownerPartnerCode: string;
  ownerCompanyName: string;
  createdAt: string;
  // null = «неизвестно» (Shanyrak не ответил), НЕ то же самое, что offline.
  isOnline: boolean | null;
  lastSeenAt: string | null;
};

type PartnerOption = {
  id: string;
  code: string;
  companyName: string;
};

// Пустой код кабинета — все кабинеты: сервер без ownerPartnerCode отдаёт
// устройства всех партнёров.
const ALL_CABINETS = "";

const PAGE_SIZE = 50;

const DEVICES_QUERY = `
  query AdminDevices($ownerPartnerCode: String, $search: String, $rtsPanels: Boolean, $limit: Int, $offset: Int) {
    adminDevices(ownerPartnerCode: $ownerPartnerCode, search: $search, rtsPanels: $rtsPanels, limit: $limit, offset: $offset) {
      id
      name
      model
      protocol
      deviceType
      motherboardId
      agentSerial
      pointId
      pointName
      gateGroupName
      ownerPartnerCode
      ownerCompanyName
      createdAt
      isOnline
      lastSeenAt
    }
    adminDevicesTotal(ownerPartnerCode: $ownerPartnerCode, search: $search, rtsPanels: $rtsPanels)
  }
`;

const PARTNERS_QUERY = `
  query AdminPartners {
    adminPartners(limit: 200, offset: 0) {
      id
      code
      companyName
    }
  }
`;

const TRANSFER_MUTATION = `
  mutation AdminTransferDevice($deviceId: UUID!, $toPartnerId: UUID!) {
    adminTransferDevice(deviceId: $deviceId, toPartnerId: $toPartnerId)
  }
`;

const IMPORT_DEVICES_MUTATION = `
  mutation AdminImportMetaDevices {
    adminImportDevices
  }
`;

const CAMERA_SERIAL_QUERY = `
  query AdminRTSCameraSerial($deviceId: UUID!) {
    adminRTSCameraSerial(deviceId: $deviceId)
  }
`;

const REPLACE_CAMERA_MODULE_MUTATION = `
  mutation AdminReplaceRTSCameraModule($deviceId: UUID!, $agentSerial: String!) {
    adminReplaceRTSCameraModule(deviceId: $deviceId, agentSerial: $agentSerial) {
      success
      notice
      agentSerial
    }
  }
`;

// Серийник модуля камеры RTS-панели — как на наклейке модуля. Окончательную
// проверку (формат, не занят ли) делает Shanyrak, здесь — подсказка до отправки.
const CAMERA_SERIAL_RE = /^EF[0-9A-F]{14}$/;

// Пояснения Shanyrak приходят со строчной буквы и без точки — в окне это
// отдельное предложение.
function sentence(text: string): string {
  const t = text.charAt(0).toUpperCase() + text.slice(1);
  return /[.!?…]$/.test(t) ? t : `${t}.`;
}

export function DevicesPage() {
  const [devices, setDevices] = useState<AdminDevice[]>([]);
  const [partners, setPartners] = useState<PartnerOption[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Кабинет в фокусе; по умолчанию — все кабинеты.
  const [partnerCode, setPartnerCode] = useState<string>(ALL_CABINETS);
  const allCabinets = partnerCode === ALL_CABINETS;

  // Выделение для пакетного переноса: id устройства → код кабинета-владельца.
  // Владельцы нужны переносу — их кабинеты не годятся в цели. Живёт поверх
  // страниц: bulk-перенос собирает id с разных страниц.
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  // Номер выбранного устройства — для окна массового обновления: выбор
  // живёт поверх страниц, и строк других страниц под рукой нет.
  const selectedLabels = useRef<Map<string, string>>(new Map());
  const [search, setSearch] = useState("");
  // «Панели Evimiz» — только RTS-панели подъезда, те, что принимают прошивку.
  const [rtsPanelsOnly, setRtsPanelsOnly] = useState(false);

  // Серверная пагинация — фильтр по кабинету/поиску и окно уходят в SQL
  // (до LIMIT). Иначе после ~1100-импорта хвост старейших устройств не
  // доезжает до браузера и его нельзя перенести.
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);

  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);

  const [transferOpen, setTransferOpen] = useState(false);
  // RTS-панель, у которой меняют модуль камеры (окно открыто).
  const [cameraModuleDevice, setCameraModuleDevice] = useState<AdminDevice | null>(null);
  // Прошивки — только у учётки с правом на них; прошивка RTS-панелей
  // текущей страницы читается одним запросом.
  const [firmwareAccess, setFirmwareAccess] = useState(false);
  const [firmwares, setFirmwares] = useState<Record<string, PanelFirmware>>({});
  const [firmwareDevice, setFirmwareDevice] = useState<AdminDevice | null>(null);
  const [bulkFirmwareOpen, setBulkFirmwareOpen] = useState(false);

  // Партнёры грузятся один раз (справочник кабинетов + dropdown).
  async function loadPartners() {
    try {
      const p = await gql<{ adminPartners: PartnerOption[] }>(PARTNERS_QUERY);
      setPartners(p.adminPartners);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  // Latest-wins: два запроса (быстрое переключение кабинета) могут прийти не
  // по порядку — применяем только ответ последнего, иначе таблица кабинета A
  // могла бы перезаписать данные под меткой кабинета B.
  const reqSeqRef = useRef(0);

  // Список устройств — серверный фильтр + окно. Дебаунс поиска — в эффекте ниже.
  async function reloadDevices(reqOffset: number) {
    const seq = ++reqSeqRef.current;
    try {
      const d = await gql<{ adminDevices: AdminDevice[]; adminDevicesTotal: number }>(
        DEVICES_QUERY,
        {
          ownerPartnerCode: partnerCode || null,
          search: search.trim() || null,
          rtsPanels: rtsPanelsOnly || null,
          limit: PAGE_SIZE,
          offset: reqOffset,
        },
      );
      if (seq !== reqSeqRef.current) return; // пришёл устаревший ответ — игнор
      // Окно уехало за конец (перенос уменьшил total на текущей странице) —
      // подтягиваем offset на последнюю валидную страницу; эффект перезагрузит.
      if (reqOffset > 0 && reqOffset >= d.adminDevicesTotal) {
        const lastPage =
          d.adminDevicesTotal <= 0
            ? 0
            : (Math.ceil(d.adminDevicesTotal / PAGE_SIZE) - 1) * PAGE_SIZE;
        setTotal(d.adminDevicesTotal);
        setOffset(lastPage);
        return;
      }
      setDevices(d.adminDevices);
      setTotal(d.adminDevicesTotal);
    } catch (err) {
      if (seq !== reqSeqRef.current) return;
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function reload() {
    await Promise.all([loadPartners(), reloadDevices(offset)]);
  }

  useEffect(() => {
    loadPartners();
    loadFirmwareAccess().then(setFirmwareAccess);
  }, []);

  useEffect(() => {
    const ids = devices.filter((d) => firmwarePanel(d.protocol, d.model)).map((d) => d.id);
    if (!firmwareAccess || ids.length === 0) {
      setFirmwares({});
      return;
    }
    let cancelled = false;
    gql<{ adminPanelFirmware: PanelFirmware[] }>(PANEL_FIRMWARE_QUERY, { deviceIds: ids })
      .then((d) => {
        if (cancelled) return;
        const byId: Record<string, PanelFirmware> = {};
        for (const fw of d.adminPanelFirmware) byId[fw.deviceId] = fw;
        setFirmwares(byId);
      })
      .catch(() => {
        // Колонка прошивки останется пустой — список устройств от этого не страдает.
        if (!cancelled) setFirmwares({});
      });
    return () => {
      cancelled = true;
    };
  }, [devices, firmwareAccess]);

  const onFirmwareChanged = useCallback((fw: PanelFirmware) => {
    setFirmwares((prev) => ({ ...prev, [fw.deviceId]: fw }));
  }, []);

  // Смена кабинета/поиска → страница в начало + сброс выделения (нельзя
  // тащить панели одного кабинета, выбрав их, потом переключившись).
  useEffect(() => {
    setSelected(new Map());
    setOffset(0);
  }, [partnerCode, search, rtsPanelsOnly]);

  // Единый fetch: любой из partnerCode/search/offset → перезагрузка окна,
  // дебаунс 300ms (не бьём SQL на каждый символ поиска; быстрые клики
  // Назад/Далее коалесятся). clearTimeout отменяет предыдущий запрос.
  useEffect(() => {
    const t = setTimeout(() => reloadDevices(offset), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerCode, search, rtsPanelsOnly, offset]);

  // Выбранный кабинет пропал из справочника (удалён) — назад ко всем.
  useEffect(() => {
    if (partners.length === 0 || allCabinets) return;
    if (!partners.some((p) => p.code === partnerCode)) {
      setPartnerCode(ALL_CABINETS);
    }
  }, [partners, partnerCode, allCabinets]);

  const currentPartner = useMemo(
    () => partners.find((p) => p.code === partnerCode) ?? null,
    [partners, partnerCode],
  );

  // Фильтрация/поиск теперь серверные — devices это уже текущая страница
  // выбранного кабинета. Клиентский фильтр убран (он резал только окно и
  // прятал хвост после большого импорта).

  // hardwareId — основной видимый идентификатор устройства в Sales-UX.
  // Партнёр узнаёт железку по нему (наклейка на корпусе), а не по uuid.
  function hardwareId(d: AdminDevice): string {
    return d.motherboardId || d.agentSerial || d.id.slice(0, 8);
  }

  // «Выбрать все» действует на ТЕКУЩУЮ страницу — выделение (selected) при
  // этом накапливается поверх страниц для bulk-переноса.
  const allPageSelected = devices.length > 0 && devices.every((d) => selected.has(d.id));

  function toggleAll() {
    const next = new Map(selected);
    if (allPageSelected) {
      for (const d of devices) next.delete(d.id);
    } else {
      for (const d of devices) {
        next.set(d.id, d.ownerPartnerCode);
        selectedLabels.current.set(d.id, hardwareId(d));
      }
    }
    setSelected(next);
  }

  function toggleOne(d: AdminDevice) {
    const next = new Map(selected);
    if (next.has(d.id)) next.delete(d.id);
    else {
      next.set(d.id, d.ownerPartnerCode);
      selectedLabels.current.set(d.id, hardwareId(d));
    }
    setSelected(next);
  }

  // Кабинеты, из которых выбраны устройства, — для подписи и для переноса.
  const selectedOwners = useMemo(() => Array.from(new Set(selected.values())), [selected]);
  const ownerLabel = (code: string) =>
    partners.find((p) => p.code === code)?.companyName ?? (code || "без кабинета");

  async function importMetaDevices() {
    setImporting(true);
    setImportMsg(null);
    setError(null);
    try {
      const res = await gql<{ adminImportDevices: number }>(IMPORT_DEVICES_MUTATION);
      const n = res.adminImportDevices;
      setImportMsg(
        n > 0
          ? `Заведено устройств: ${n}. Каждое попало в кабинет того партнёра, в чьей компании оно числится в Shanyrak — переключи кабинет, чтобы увидеть.`
          : "Расхождений нет — всё, что есть в Shanyrak, уже заведено",
      );
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setImporting(false);
    }
  }

  return (
    <div style={{ paddingBottom: selected.size > 0 ? 80 : 0 }}>
      {/* Шапка: селектор кабинета + импорт */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="row" style={{ justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flex: 1, minWidth: 280 }}>
            <label style={{ fontWeight: 600 }}>Кабинет:</label>
            <select
              value={partnerCode}
              onChange={(e) => setPartnerCode(e.target.value)}
              style={{ flex: 1, maxWidth: 420 }}
            >
              <option value={ALL_CABINETS}>Все кабинеты</option>
              {partners.map((p) => (
                <option key={p.id} value={p.code}>
                  {p.companyName} ({p.code})
                </option>
              ))}
            </select>
          </div>
          <button onClick={importMetaDevices} disabled={importing}>
            {importing ? "Подтягиваем…" : "Свести устройства с Shanyrak"}
          </button>
        </div>

        {importMsg && (
          <div className="muted" style={{ marginTop: 12, fontSize: 13 }}>
            {importMsg}
          </div>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {/* Подзаголовок + поиск */}
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 12, gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>
          {allCabinets
            ? `Все устройства (${total})`
            : `Устройства партнёра «${currentPartner?.companyName ?? "—"}» (${total})`}
        </h2>
        <div className="row" style={{ gap: 12, alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, whiteSpace: "nowrap" }}>
            <input type="checkbox" checked={rtsPanelsOnly} onChange={(e) => setRtsPanelsOnly(e.target.checked)} />
            Панели Evimiz
          </label>
          <input
            type="search"
            placeholder="Номер, адрес, точка, кабинет, id"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ minWidth: 280 }}
          />
        </div>
      </div>

      <div className="card">
        {devices.length === 0 ? (
          <div className="muted">
            {search.trim() !== "" || rtsPanelsOnly
              ? "Под поиск и фильтр ничего не подходит."
              : allCabinets
                ? "Устройств пока нет."
                : "У этого кабинета устройств пока нет."}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: 32 }}>
                  <input
                    type="checkbox"
                    checked={allPageSelected}
                    onChange={toggleAll}
                    title="Выбрать все на странице"
                  />
                </th>
                <th>Номер устройства / точка</th>
                {allCabinets && <th>Кабинет</th>}
                <th>Модель</th>
                <th>Протокол</th>
                <th>Связь</th>
                {firmwareAccess && <th>Прошивка</th>}
                <th>Создан</th>
              </tr>
            </thead>
            <tbody>
              {devices.map((d) => (
                <tr
                  key={d.id}
                  style={{ background: selected.has(d.id) ? "rgba(80,160,80,0.08)" : undefined }}
                >
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(d.id)}
                      onChange={() => toggleOne(d)}
                    />
                  </td>
                  <td>
                    <div style={{ fontFamily: "monospace", fontWeight: 600 }}>
                      {hardwareId(d)}
                    </div>
                    <div className="muted" style={{ fontSize: 12 }}>
                      {d.pointName ||
                        (d.gateGroupName ? `Калитка · ${d.gateGroupName}` : "— не привязано")}
                    </div>
                    {/* Серийник камеры — под номером панели; когда номера
                        нет, он уже стоит в hardwareId. */}
                    {d.protocol === "rts" && d.motherboardId && d.agentSerial && (
                      <div className="muted" style={{ fontSize: 12, fontFamily: "monospace" }}>
                        камера {d.agentSerial}
                      </div>
                    )}
                    {d.protocol === "rts" && (
                      <button
                        type="button"
                        className="secondary"
                        style={{ marginTop: 6, padding: "2px 8px", fontSize: 12 }}
                        onClick={() => setCameraModuleDevice(d)}
                        title="Вписать серийник нового модуля камеры после замены"
                      >
                        Модуль камеры
                      </button>
                    )}
                    {firmwarePanel(d.protocol, d.model) && firmwareAccess && (
                      <button
                        type="button"
                        className="secondary"
                        style={{ marginTop: 6, marginLeft: 6, padding: "2px 8px", fontSize: 12 }}
                        onClick={() => setFirmwareDevice(d)}
                        title="Версия, отметка «стенд», запуск обновления"
                      >
                        Прошивка
                      </button>
                    )}
                  </td>
                  {allCabinets && (
                    <td>
                      <div>{d.ownerCompanyName || "—"}</div>
                      {d.ownerPartnerCode && (
                        <div className="muted" style={{ fontSize: 12, fontFamily: "monospace" }}>
                          {d.ownerPartnerCode}
                        </div>
                      )}
                    </td>
                  )}
                  <td className="muted">{d.model || "—"}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{d.protocol}</td>
                  <td><DeviceLiveStatus isOnline={d.isOnline} lastSeenAt={d.lastSeenAt} /></td>
                  {firmwareAccess && (
                    <td style={{ fontSize: 12 }}>
                      <FirmwareCell fw={firmwares[d.id]} rts={firmwarePanel(d.protocol, d.model)} />
                    </td>
                  )}
                  <td className="muted">{new Date(d.createdAt).toLocaleDateString("ru-RU")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Пагинация. total из adminDevicesTotal с тем же фильтром — окно
          страницы серверное, так что хвост после большого импорта доступен. */}
      {total > PAGE_SIZE && (
        <div
          className="row"
          style={{ justifyContent: "space-between", alignItems: "center", marginTop: 12, gap: 12 }}
        >
          <span className="muted" style={{ fontSize: 13 }}>
            {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} из {total}
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className="secondary"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Назад
            </button>
            <button
              className="secondary"
              disabled={offset + PAGE_SIZE >= total}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Далее
            </button>
          </div>
        </div>
      )}

      {/* Sticky-bar выделения: повторяет «Выбрано N: [→]» из Спутник-кабинета. */}
      {selected.size > 0 && (
        <div
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            background: "var(--bg-elevated)",
            color: "var(--text)",
            borderTop: "1px solid rgba(255,255,255,0.12)",
            boxShadow: "0 -4px 12px rgba(0,0,0,0.35)",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            zIndex: 50,
          }}
        >
          <div>
            <strong>Выбрано {selected.size} устройств</strong>
            <span className="muted" style={{ marginLeft: 8, fontSize: 13 }}>
              {selectedOwners.length === 1
                ? `из кабинета «${ownerLabel(selectedOwners[0])}»`
                : `из кабинетов: ${selectedOwners.map(ownerLabel).join(", ")}`}
            </span>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="secondary" onClick={() => setSelected(new Map())}>
              Снять выделение
            </button>
            {firmwareAccess && (
              <button className="secondary" onClick={() => setBulkFirmwareOpen(true)}>Обновить прошивку</button>
            )}
            <button onClick={() => setTransferOpen(true)}>Перенести в другой кабинет</button>
          </div>
        </div>
      )}

      {firmwareDevice && (
        <FirmwareModal
          deviceId={firmwareDevice.id}
          title={hardwareId(firmwareDevice)}
          onClose={() => setFirmwareDevice(null)}
          onChanged={onFirmwareChanged}
        />
      )}

      {bulkFirmwareOpen && (
        <BulkFirmwareModal
          deviceIds={Array.from(selected.keys())}
          labels={selectedLabels.current}
          onClose={() => setBulkFirmwareOpen(false)}
          onChanged={onFirmwareChanged}
        />
      )}

      {cameraModuleDevice && (
        <CameraModuleModal
          device={cameraModuleDevice}
          title={hardwareId(cameraModuleDevice)}
          onClose={() => setCameraModuleDevice(null)}
        />
      )}

      {transferOpen && (
        <BulkTransferModal
          partners={partners}
          ownerCodes={selectedOwners}
          deviceIds={Array.from(selected.keys())}
          onClose={(remainingIds) => {
            // Закрытие после (частичного) сбоя: сохраняем выделение на
            // непереехавших, чтобы можно было повторить, и обновляем список.
            setTransferOpen(false);
            const left = new Set(remainingIds);
            setSelected(new Map(Array.from(selected).filter(([id]) => left.has(id))));
            reload();
          }}
          onDone={(summary) => {
            setTransferOpen(false);
            setSelected(new Map());
            setImportMsg(summary);
            reload();
          }}
        />
      )}
    </div>
  );
}

function BulkTransferModal({
  partners,
  ownerCodes,
  deviceIds,
  onClose,
  onDone,
}: {
  partners: PartnerOption[];
  // Кабинеты-владельцы выбранных устройств.
  ownerCodes: string[];
  deviceIds: string[];
  // onClose передаёт наверх id, которые ЕЩЁ не перенеслись (сбойные +
  // нетронутые) — родитель восстанавливает по ним выделение для повтора.
  onClose: (remainingIds: string[]) => void;
  onDone: (summary: string) => void;
}) {
  const [toPartnerId, setToPartnerId] = useState("");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number; failed: number }>(
    { done: 0, total: deviceIds.length, failed: 0 },
  );
  const [errors, setErrors] = useState<string[]>([]);
  // Оставшиеся к переносу id: изначально все, после запуска — только сбойные.
  const [pendingIds, setPendingIds] = useState<string[]>(deviceIds);

  // Кабинеты-цели — без владельцев выбранного: перенос самому себе не пустой,
  // он стёр бы с панели доступы его же жильцов (сервер такой перенос тоже
  // отвергает).
  const targets = partners.filter((p) => !ownerCodes.includes(p.code));

  // runTransfer гоняет перенос по переданному списку id. Partial-fail НЕ
  // считается успехом: модалка остаётся открытой со списком ошибок и кнопкой
  // «Повторить для несбойных», пока все не пройдут (тогда onDone) — иначе
  // детали сбоя стирались при закрытии и повторить было нельзя.
  async function runTransfer(ids: string[]) {
    if (!toPartnerId || ids.length === 0) return;
    setRunning(true);
    setErrors([]);
    setProgress({ done: 0, total: ids.length, failed: 0 });

    let done = 0;
    let failed = 0;
    const collectedErrors: string[] = [];
    const collectedFailedIds: string[] = [];
    await Promise.all(
      ids.map(async (deviceId) => {
        try {
          await gql(TRANSFER_MUTATION, { deviceId, toPartnerId });
        } catch (err) {
          failed += 1;
          collectedFailedIds.push(deviceId);
          collectedErrors.push(
            `${deviceId.slice(0, 8)}…: ${err instanceof Error ? err.message : String(err)}`,
          );
        } finally {
          done += 1;
          setProgress({ done, total: ids.length, failed });
        }
      }),
    );
    setErrors(collectedErrors);
    setPendingIds(collectedFailedIds);
    setRunning(false);

    if (failed === 0) {
      onDone(`Перенесено устройств: ${ids.length}`);
    }
    // failed > 0 (partial ИЛИ full): модалка остаётся открытой, onDone НЕ
    // зовётся — детали ошибок видны, доступен повтор для сбойных.
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    await runTransfer(pendingIds);
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
      }}
      onClick={running ? undefined : () => onClose(pendingIds)}
    >
      <form
        className="card"
        style={{ maxWidth: 520, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h3 style={{ margin: "0 0 12px 0" }}>Перепривязка панелей</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Переносим <strong>{deviceIds.length}</strong> устройств в другой кабинет.
          На каждой панели сбрасываются ключи/коды/лица и привязка к точке установки —
          новый партнёр привяжет к своим точкам сам.
        </p>

        <div className="form-row">
          <label>Новый владелец *</label>
          <select
            required
            value={toPartnerId}
            onChange={(e) => setToPartnerId(e.target.value)}
            disabled={running}
          >
            <option value="">Выберите кабинет…</option>
            {targets.map((p) => (
              <option key={p.id} value={p.id}>
                {p.companyName} ({p.code})
              </option>
            ))}
          </select>
        </div>

        {running && (
          <div className="muted" style={{ fontSize: 13, marginTop: 8 }}>
            Перенесено: {progress.done} / {progress.total}
            {progress.failed > 0 && <> · ошибок: {progress.failed}</>}
          </div>
        )}

        {errors.length > 0 && !running && (
          <div className="error-banner" style={{ marginTop: 12, maxHeight: 160, overflow: "auto" }}>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>
              Не перенесено: {pendingIds.length}. Можно повторить.
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12 }}>
              {errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button
            type="button"
            className="secondary"
            onClick={() => onClose(pendingIds)}
            disabled={running}
          >
            Закрыть
          </button>
          {!running && pendingIds.length > 0 && errors.length > 0 && (
            <button type="button" onClick={() => runTransfer(pendingIds)} disabled={!toPartnerId}>
              Повторить для несбойных ({pendingIds.length})
            </button>
          )}
          {errors.length === 0 && (
            <button type="submit" disabled={running || !toPartnerId}>
              {running ? "Переносим…" : "Перепривязать"}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}

/**
 * Замена модуля камеры RTS-панели. После замены модуля у панели новый
 * серийник камеры (наклейка на модуле). Пока его не вписали, Shanyrak не
 * находит видео панели, а распознавание лиц ждёт событий от старой камеры.
 * Серийник проверяет и пишет Shanyrak; GEO после записи сразу обновляет
 * галерею распознавания лиц.
 */
function CameraModuleModal({
  device,
  title,
  onClose,
}: {
  device: AdminDevice;
  title: string;
  onClose: () => void;
}) {
  const [current, setCurrent] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [serial, setSerial] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    gql<{ adminRTSCameraSerial: string | null }>(CAMERA_SERIAL_QUERY, { deviceId: device.id })
      .then((d) => {
        if (!cancelled) setCurrent(d.adminRTSCameraSerial);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [device.id]);

  const normalized = serial.trim().toUpperCase();
  const formatOk = CAMERA_SERIAL_RE.test(normalized);
  const same = current !== null && normalized === current;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!formatOk || same || saving) return;
    setSaving(true);
    setResult(null);
    try {
      const d = await gql<{
        adminReplaceRTSCameraModule: { success: boolean; notice: string | null; agentSerial: string | null };
      }>(REPLACE_CAMERA_MODULE_MUTATION, { deviceId: device.id, agentSerial: normalized });
      const r = d.adminReplaceRTSCameraModule;
      if (r.success) {
        if (r.agentSerial) setCurrent(r.agentSerial);
        setSerial("");
        setResult({
          ok: true,
          text:
            `Серийник записан: ${r.agentSerial ?? normalized}. Распознавание лиц перейдёт на него в течение минуты.` +
            (r.notice ? ` ${sentence(r.notice)}` : ""),
        });
      } else {
        setResult({ ok: false, text: r.notice ? sentence(r.notice) : "Shanyrak отказал без пояснения" });
      }
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        display: "grid",
        placeItems: "center",
        zIndex: 100,
      }}
      onClick={saving ? undefined : onClose}
    >
      <form
        className="card"
        style={{ maxWidth: 520, width: "90%" }}
        onClick={(e) => e.stopPropagation()}
        onSubmit={onSubmit}
      >
        <h3 style={{ margin: "0 0 12px 0" }}>Модуль камеры панели {title}</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          После замены модуля камеры впишите серийник нового модуля — EF и 14 символов с наклейки.
          Видео панели и распознавание лиц перейдут на него. Ключи, квартиры, история и привязка
          панели не меняются.
        </p>

        <div className="form-row">
          <label>Сейчас у панели</label>
          <div style={{ fontFamily: "monospace" }}>
            {loading ? "загружаем…" : loadError ? <span className="muted">{loadError}</span> : current ?? "— серийника нет"}
          </div>
        </div>

        <div className="form-row">
          <label>Серийник нового модуля *</label>
          <input
            value={serial}
            onChange={(e) => setSerial(e.target.value.toUpperCase())}
            placeholder="EF0000000603A1B2"
            maxLength={24}
            autoFocus
            disabled={saving}
            style={{ fontFamily: "monospace" }}
          />
          {serial.trim() !== "" && !formatOk && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Нужно EF и 14 символов 0–9 или A–F.
            </div>
          )}
          {same && (
            <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
              Этот серийник уже у панели.
            </div>
          )}
        </div>

        {result && (
          <div
            className={result.ok ? "muted" : "error-banner"}
            style={{ marginTop: 12, fontSize: 13, color: result.ok ? "#22c55e" : undefined }}
          >
            {result.text}
          </div>
        )}

        <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" className="secondary" onClick={onClose} disabled={saving}>
            Закрыть
          </button>
          <button type="submit" disabled={saving || !formatOk || same}>
            {saving ? "Записываем…" : "Записать"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * Индикатор связи с панелью.
 *
 * Три состояния, а не два. `null` — это «Shanyrak не ответил / токен партнёра
 * не расшифровался», и показывать его как «офлайн» нельзя: живую панель
 * объявили бы мёртвой, а именно по этому индикатору принимают решение
 * «выезжать на объект или нет».
 */
function DeviceLiveStatus({
  isOnline,
  lastSeenAt,
}: {
  isOnline: boolean | null;
  lastSeenAt: string | null;
}) {
  if (isOnline === null || isOnline === undefined) {
    return (
      <span className="muted" title="Статус не получен — Shanyrak недоступен или нет доступа к компании партнёра">
        нет данных
      </span>
    );
  }

  const seen = lastSeenAt ? new Date(lastSeenAt) : null;
  const seenLabel = seen
    ? seen.toLocaleString("ru-RU", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <span
      style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}
      title={seenLabel ? `Последняя связь: ${seenLabel}` : undefined}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: isOnline ? "#22c55e" : "#ef4444",
          flexShrink: 0,
        }}
      />
      <span style={{ color: isOnline ? "inherit" : "#ef4444" }}>
        {isOnline ? "онлайн" : "офлайн"}
      </span>
      {!isOnline && seenLabel && (
        <span className="muted" style={{ fontSize: 12 }}>с {seenLabel}</span>
      )}
    </span>
  );
}

// Прошивка в списке: версия со слов панели, отметка «стенд» и идущее или
// последнее обновление.
function FirmwareCell({ fw, rts }: { fw: PanelFirmware | undefined; rts: boolean }) {
  if (!rts) return <span className="muted">—</span>;
  if (!fw) return <span className="muted">нет данных</span>;
  const u = fw.update;
  return (
    <div>
      <span style={{ fontFamily: "monospace" }}>{fw.swVersion ?? "—"}</span>
      {fw.testbench && <span className="muted"> · стенд</span>}
      {u && (phaseActive(u.phase) || u.phase === "FAILED") && (
        <div style={{ color: u.phase === "FAILED" ? "#ef4444" : undefined }}>
          → {u.target.version}: {PHASE_LABEL[u.phase]}
        </div>
      )}
    </div>
  );
}
